import { useState, useCallback } from 'react';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import { Card, CardHeader, CardBody, Button, Input, Label } from '../components/ui';
import { HiPlay, HiSearch, HiDownload, HiClipboardList, HiDatabase } from '../components/icons';
import { useSelectedTenant } from '../stores/tenantStore';
import { showToast } from '../utils/toast';
import { juniperDataService, type JuniperDataResult } from '../services/juniper-data.service';

interface FormData {
  operationalBillId: string;
  trrBillId: string;
}

interface FormErrors {
  operationalBillId?: string;
  trrBillId?: string;
  general?: string;
}

export function JuniperDataPage() {
  const { selectedTenant } = useSelectedTenant();
  const [formData, setFormData] = useState<FormData>({ operationalBillId: '', trrBillId: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [results, setResults] = useState<JuniperDataResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field] || errors.general) {
      setErrors(prev => ({ ...prev, [field]: undefined, general: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!selectedTenant) {
      setErrors({ general: 'Please select a tenant first' });
      return;
    }

    const hasOperationalId = formData.operationalBillId.trim() !== '';
    if (!hasOperationalId) {
      setErrors({ operationalBillId: 'Operational Bill ID is required' });
      return;
    }

    const opId = Number(formData.operationalBillId);
    if (isNaN(opId) || opId <= 0) {
      setErrors({ operationalBillId: 'Please enter a valid positive number' });
      return;
    }

    const hasTrr = formData.trrBillId.trim() !== '';
    if (hasTrr) {
      const trrId = Number(formData.trrBillId);
      if (isNaN(trrId) || trrId <= 0) {
        setErrors({ trrBillId: 'Please enter a valid positive number' });
        return;
      }
    }

    setIsLoading(true);
    try {
      const data = await juniperDataService.fetchJuniperData(selectedTenant.id, {
        operationalBillId: opId,
        ...(hasTrr && { trrBillId: Number(formData.trrBillId) }),
        includePaymentLink: true,
      });
      setResults(data);
      sessionStorage.setItem('juniperData', JSON.stringify(data));
      showToast.success('Juniper data fetched successfully!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch Juniper data';
      setErrors({ general: message });
      setResults(null);
      showToast.error(`Fetch failed: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadJson = () => {
    if (!results) return;
    const dataStr = JSON.stringify(results, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `juniper-data-${selectedTenant?.id}-${formData.operationalBillId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast.success('JSON file downloaded!');
  };

  const copyToClipboard = async () => {
    if (!results) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
      showToast.success('JSON copied to clipboard!');
    } catch {
      showToast.error('Failed to copy to clipboard');
    }
  };

  const searchInJson = useCallback((obj: unknown, term: string, path: string = ''): boolean => {
    if (!term) return true;
    const lowerTerm = term.toLowerCase();
    if (path.toLowerCase().includes(lowerTerm)) return true;
    if (typeof obj === 'string' && obj.toLowerCase().includes(lowerTerm)) return true;
    if (typeof obj === 'number' && obj.toString().includes(term)) return true;
    if (typeof obj === 'object' && obj !== null) {
      for (const [key, value] of Object.entries(obj)) {
        if (searchInJson(value, term, path ? `${path}.${key}` : key)) return true;
      }
    }
    return false;
  }, []);

  const filteredData =
    results && searchTerm
      ? Object.fromEntries(Object.entries(results).filter(([key, value]) => searchInJson(value, searchTerm, key)))
      : results;

  const shouldExpandNode = useCallback(
    (_level: number, value: unknown, field?: string): boolean => {
      if (!searchTerm) return true;
      if (typeof value === 'object' && value !== null) {
        return searchInJson(value, searchTerm, field || '');
      }
      return false;
    },
    [searchTerm, searchInJson]
  );

  if (!selectedTenant) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">Juniper Data</h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-8">
          Fetch Juniper data from Clio for a specific tenant. Provide an Operational Bill ID to retrieve the full data object.
        </p>
        <Card padding="lg" variant="outlined">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <HiDatabase className="w-12 h-12 text-xtnd-dark-300 mb-4" />
            <h3 className="text-lg font-medium text-xtnd-dark-700 dark:text-xtnd-dark-200 mb-2">
              No Tenant Selected
            </h3>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 max-w-md">
              Please select a tenant from the dropdown in the navigation bar to fetch Juniper data.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">Juniper Data</h1>
      <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-8">
        Fetch Juniper data from Clio for testing templates. Provide an Operational Bill ID to retrieve the full data object.
      </p>

      <Card padding="lg" variant="outlined" className="mb-6">
        <CardHeader>
          <h2 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">Fetch Data</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tenant Info */}
            <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50 p-3 rounded-md">
              <p className="text-sm text-xtnd-dark-700 dark:text-xtnd-dark-200">
                <strong>Tenant:</strong> {selectedTenant.name} ({selectedTenant.id})
              </p>
            </div>

            {/* Input Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="operationalBillId">
                  Operational Bill ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="operationalBillId"
                  type="number"
                  value={formData.operationalBillId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('operationalBillId', e.target.value)}
                  placeholder="e.g., 1289908750"
                  disabled={isLoading}
                />
                {errors.operationalBillId && (
                  <p className="text-sm text-red-600 mt-1">{errors.operationalBillId}</p>
                )}
              </div>
              <div>
                <Label htmlFor="trrBillId">
                  Trust Request Bill ID <span className="text-xs text-xtnd-dark-400 ml-1">(Optional)</span>
                </Label>
                <Input
                  id="trrBillId"
                  type="number"
                  value={formData.trrBillId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('trrBillId', e.target.value)}
                  placeholder="e.g., 1191860067"
                  disabled={isLoading}
                />
                {errors.trrBillId && (
                  <p className="text-sm text-red-600 mt-1">{errors.trrBillId}</p>
                )}
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full"
            >
              <HiPlay className="w-4 h-4 mr-2" />
              {isLoading ? 'Fetching Data...' : 'Fetch Juniper Data'}
            </Button>
          </form>
        </CardBody>
      </Card>

      {/* Results */}
      {results && (
        <Card padding="lg" variant="outlined">
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <h2 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">
                Juniper Data Results
              </h2>
              <div className="flex space-x-2">
                <Button variant="secondary" size="sm" onClick={copyToClipboard}>
                  <HiClipboardList className="w-4 h-4 mr-1" />
                  Copy
                </Button>
                <Button variant="secondary" size="sm" onClick={downloadJson}>
                  <HiDownload className="w-4 h-4 mr-1" />
                  Download JSON
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiSearch className="h-4 w-4 text-xtnd-dark-300" />
              </div>
              <Input
                type="text"
                placeholder="Search JSON data... (e.g., 'bank_transactions', 'amount', 'client_name')"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* JSON Viewer */}
            <div className="bg-white dark:bg-xtnd-dark-700 border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-md p-4 max-h-[600px] overflow-auto">
              <JsonView
                data={filteredData || {}}
                shouldExpandNode={shouldExpandNode}
                style={{
                  ...defaultStyles,
                  container: 'json-container font-mono text-sm',
                  label: 'text-xtnd-blue font-medium',
                  stringValue: 'text-green-600',
                  numberValue: 'text-blue-600',
                  booleanValue: 'text-purple-600',
                  nullValue: 'text-xtnd-dark-400',
                  punctuation: 'text-xtnd-dark-500',
                }}
                clickToExpandNode={true}
              />
            </div>

            {/* Search indicator */}
            {searchTerm && (
              <div className="text-xs text-xtnd-dark-400 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 p-2 rounded">
                <strong>Search active:</strong> Showing results for "{searchTerm}"
                {Object.keys(filteredData || {}).length === 0 && (
                  <span className="text-red-500 ml-2">No matches found</span>
                )}
              </div>
            )}

            {/* Summary */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-md">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <strong>Op Bill:</strong> {(results.operational_bill as any)?.id || formData.operationalBillId}
                </div>
                <div>
                  <strong>Matter:</strong> {(results.matter as any)?.display_number || '—'}
                </div>
                <div>
                  <strong>Trust Txns:</strong> {results.trust?.bank_transactions?.length || 0}
                </div>
                <div>
                  <strong>WIP Amount:</strong> {results.matterWIPAmount != null ? `$${(results.matterWIPAmount / 100).toFixed(2)}` : '—'}
                </div>
              </div>
              <div className="mt-2 text-xs text-green-700 dark:text-green-300">
                Data saved to session storage for use in Template Testing
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
